/**
 * Hook that resolves location/product names for title generation.
 *
 * Uses O(1) indexed lookups instead of linear array scans,
 * and memoizes the resolved result to avoid recalculation.
 */

import { useMemo } from "react";
import type { ProductClass } from "../types/enums";
import { useLocationLookups, useProductLookups, findLocationById, findProductById } from "./useLookups";
import { getLocationLevelFromStringLocationId, getLocationDisplayName } from "../utils/locationUtils";
import { worldGroupDatum } from "../graphql/queries/getLocationsMetadata";
import { allProductsDatum } from "../graphql/queries/getProductsMetadata";
import { determineIfHasIntraRegionTrade } from "../utils/titleGeneration";

export interface TitleMetadata {
  exporterName: string | undefined;
  importerName: string | undefined;
  productName: string | undefined;
  isWorldExporter: boolean;
  isWorldImporter: boolean;
  hasIntraRegionTrade: boolean;
  loading: boolean;
}

/**
 * Resolves location and product display names for visualization titles.
 *
 * Replaces the manual `.find()` scans in VisualizationTitleGenerator with
 * O(1) indexed lookups from useLocationLookups/useProductLookups.
 */
export function useTitleMetadata({
  exporter,
  importer,
  product,
  productClass,
  servicesClass,
}: {
  exporter: string;
  importer: string;
  product: string;
  productClass: ProductClass;
  servicesClass?: string;
}): TitleMetadata {
  const locationResult = useLocationLookups();
  const productResult = useProductLookups(productClass, { servicesClass });

  const locationLookups = locationResult.status === "success" ? locationResult.data : undefined;
  const productLookups = productResult.status === "success" ? productResult.data : undefined;
  const loading = locationResult.status === "loading" || productResult.status === "loading";

  return useMemo(() => {
    const isWorldExporter = exporter === worldGroupDatum.groupId;
    const isWorldImporter = importer === worldGroupDatum.groupId;

    const exporterLocationLevel = getLocationLevelFromStringLocationId(exporter);
    const importerLocationLevel = getLocationLevelFromStringLocationId(importer);

    // Resolve exporter name
    let exporterName: string | undefined;
    if (isWorldExporter) {
      exporterName = "the " + worldGroupDatum.groupName;
    } else if (locationLookups) {
      const exporterDatum = findLocationById(locationLookups, exporter);
      exporterName = getLocationDisplayName(exporterDatum as Record<string, unknown>, exporterLocationLevel);
    }

    // Resolve importer name
    let importerName: string | undefined;
    if (isWorldImporter) {
      importerName = "the " + worldGroupDatum.groupName;
    } else if (locationLookups) {
      const importerDatum = findLocationById(locationLookups, importer);
      importerName = getLocationDisplayName(importerDatum as Record<string, unknown>, importerLocationLevel);
    }

    // Resolve product name
    let productName: string | undefined;
    if (product === allProductsDatum.productId) {
      productName = allProductsDatum.nameShortEn;
    } else if (productLookups) {
      const productDatum = findProductById(productLookups, product);
      productName = productDatum?.nameShortEn ?? undefined;
    }

    // Check intra-region trade
    let hasIntraRegionTrade = false;
    if (locationLookups && !isWorldExporter && !isWorldImporter) {
      const exporterDatum = findLocationById(locationLookups, exporter);
      const importerDatum = findLocationById(locationLookups, importer);
      if (exporterDatum && importerDatum) {
        hasIntraRegionTrade = determineIfHasIntraRegionTrade({
          exporter,
          exporterLocationLevel,
          foundMetadatumForExporter: exporterDatum as Record<string, unknown>,
          importer,
          importerLocationLevel,
          foundMetadatumForImporter: importerDatum as Record<string, unknown>,
        });
      }
    }

    return {
      exporterName,
      importerName,
      productName,
      isWorldExporter,
      isWorldImporter,
      hasIntraRegionTrade,
      loading,
    };
  }, [
    exporter,
    importer,
    product,
    locationLookups,
    productLookups,
    loading,
  ]);
}
